"use client"

import { useEffect, useRef, useState } from "react"
import { APIResponse } from "@/types/drivers/api"
import formatReviewDate from "@/util/drivers/utils"
import Image from "next/image"
import { AiFillStar } from "react-icons/ai"
import { Anchor } from "src/app/components/anchor"
import { RelativeTime } from "@/components/relative-time"
import QuoteCTA from "./quoteCTA"
import { normalizeReviewAuthorName, redactEmails } from "@/lib/reviews/archive"
type Car = Pick<
  APIResponse["data"]["cars"][number],
  | "id"
  | "variant_year"
  | "variant_type"
  | "model_name"
  | "make_name"
  | "metadata"
  | "images"
>
type Reviews = APIResponse["data"]["reviews"]
type Photos = APIResponse["data"]["userPhotos"]
interface PetTravelPageProps {
  photos: Photos
  cars: Car[]
  reviews: Reviews
  ctaUrl?: string
  ctaLabel?: string
}

const reviewDate = undefined

export const formatMakeName = (name: string | null | undefined): string => {
  if (!name) {
    return ""
  }

  if (/[a-z]/.test(name)) {
    return name
  }

  return name
    .split(/([ -])/)
    .map((part) => {
      if (!part.trim()) {
        return part
      }

      if (/^[A-Z0-9]{1,3}$/.test(part)) {
        return part
      }

      if (/^[A-Z0-9]+$/.test(part)) {
        return part.charAt(0) + part.slice(1).toLowerCase()
      }

      return part
    })
    .join("")
}

const VehicleDetails = ({ details }: { details: string }) => {
  const ref = useRef<HTMLParagraphElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [canExpand, setCanExpand] = useState(false)

  useEffect(() => {
    const el = ref.current

    if (!el) {
      return
    }

    setCanExpand(el.scrollHeight > el.clientHeight + 1)
  }, [details])

  return (
    <>
      <p
        ref={ref}
        className={`mt-2 text-sm text-gray-600 whitespace-pre-line transition-all ${
          expanded ? "" : "line-clamp-3"
        }`}
      >
        {details}
      </p>
      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-sm text-blue-500 hover:underline"
        >
          {expanded ? "Hide Details" : "Read More"}
        </button>
      )}
    </>
  )
}

const PetTravelPage = ({
  photos,
  cars,
  reviews,
  ctaUrl,
  ctaLabel,
}: PetTravelPageProps) => {
  return (
    <section>
      {photos?.length > 0 && (
        <div className="max-w-6xl mx-auto py-8">
          <h2 className="text-2xl font-semibold mb-4">Photos</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos?.slice(0, 6).map((photo: Photos[number], index: number) => (
              <div key={photo.id} className="aspect-square relative">
                <Image
                  src={photo.urls.large}
                  alt={`Pet travel photo ${index + 1}`}
                  fill
                  className="object-cover rounded-lg"
                />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-gray-50 rounded-xl p-8 mb-12 mt-8 border border-green7">
        <h2 className="text-2xl font-bold mb-6">We've got you covered</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="space-y-3">
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M23.9847 39.9956C26.6266 39.9956 28.0677 41.6855 30.7096 41.6855C35.9936 41.6855 35.9936 35.4087 33.3516 32.2704C30.7096 29.132 29.2686 22.8552 23.9847 22.8552C18.7007 22.8552 17.2597 29.132 14.6177 32.2704C11.9757 35.4087 11.9757 41.6855 17.2597 41.6855C20.1418 41.4441 21.3427 39.9956 23.9847 39.9956Z"
                fill="#109009"
              />
              <path
                d="M17.1179 19.1583C19.7318 18.7024 21.3727 15.5638 20.783 12.1482C20.1933 8.73261 17.5962 6.33337 14.9822 6.78935C12.3683 7.24534 10.7274 10.3839 11.3171 13.7995C11.9068 17.2151 14.5039 19.6143 17.1179 19.1583Z"
                fill="#109009"
              />
              <path
                d="M9.12074 30.3421C11.1479 29.7101 12.1508 27.134 11.3609 24.5883C10.571 22.0426 8.2874 20.4912 6.26027 21.1232C4.23315 21.7552 3.23018 24.3313 4.02008 26.877C4.80997 29.4227 7.09362 30.9741 9.12074 30.3421Z"
                fill="#109009"
              />
              <path
                d="M36.545 13.8794C37.1347 10.4638 35.4938 7.32524 32.8798 6.86925C30.2659 6.41326 27.6688 8.8125 27.0791 12.2281C26.4893 15.6437 28.1302 18.7822 30.7442 19.2382C33.3581 19.6942 35.9552 17.295 36.545 13.8794Z"
                fill="#109009"
              />
              <path
                d="M44.1678 26.9458C44.9558 24.3995 43.9516 21.8217 41.925 21.1881C39.8983 20.5545 37.6167 22.1051 36.8288 24.6514C36.0408 27.1978 37.045 29.7756 39.0716 30.4092C41.0983 31.0427 43.3799 29.4922 44.1678 26.9458Z"
                fill="#109009"
              />
            </svg>

            <h3 className="text-lg font-semibold">Pet Protection Coverage</h3>
            <p className="text-gray-600">
              Safety comes first! Your pets are covered with our $1,000 Pet
              Protection coverage.
            </p>
          </div>
          <div className="space-y-3">
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M23.8387 2.69745H24.0309C26.8995 2.72539 29.6514 3.83439 31.6781 5.79934C33.7044 7.76399 34.8394 10.4254 34.8131 13.2028V20.0139H42.1034V44.4702H5.55713V20.0139H12.847V13.0012C12.877 10.2203 14.0687 7.57798 16.138 5.6511C18.203 3.72832 20.9734 2.67272 23.8387 2.69745ZM12.847 23.69L16.588 23.6903L31.0721 23.69L34.8131 23.6903L38.3624 23.69V40.7941H9.29814V23.69H12.847ZM31.0721 20.0139H16.588V13.0311C16.6095 11.2687 17.3652 9.57281 18.7112 8.31942C20.0618 7.06187 21.8956 6.3553 23.8121 6.37342L24.0025 6.3735C25.9157 6.39434 27.7299 7.13558 29.0506 8.41607C30.3712 9.69644 31.0907 11.4094 31.0722 13.175L31.0721 20.0139Z"
                fill="#083DC4"
              />
            </svg>

            <h3 className="text-lg font-semibold">Booking Assurance</h3>
            <p className="text-gray-600">
              Stay protected with our dedicated support, refund policy and a
              driver replacement guarantee.
            </p>
          </div>
          <div className="space-y-3">
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_2002_1707)">
                <path
                  d="M33.3521 37.756H9.83128V13.8039H23.5518V9.88379H9.83128C7.6752 9.88379 5.91113 11.6479 5.91113 13.8039V37.3248C5.91113 39.4809 7.6752 41.2449 9.83128 41.2449H33.3521C35.5082 41.2449 37.2723 39.4809 37.2723 37.3248V23.6043H33.3521V37.756Z"
                  fill="#927C07"
                />
                <path
                  d="M27.472 17.7241H13.7515V21.6443H27.472V17.7241Z"
                  fill="#927C07"
                />
                <path
                  d="M13.7515 23.6042V27.5244H29.432V23.6042H23.5518H13.7515Z"
                  fill="#927C07"
                />
                <path
                  d="M29.432 29.4846H13.7515V33.4048H29.432V29.4846Z"
                  fill="#927C07"
                />
                <path
                  d="M33.4952 14.1434L30.8656 11.5138L28.0823 14.2775L33.4952 19.6904L44.5422 8.64334L41.7785 5.87964L33.4952 14.1434Z"
                  fill="#927C07"
                />
              </g>
              <defs>
                <clipPath id="clip0_2002_1707">
                  <rect
                    width="47.0417"
                    height="47.0417"
                    fill="white"
                    transform="translate(0.0310059 0.0834961)"
                  />
                </clipPath>
              </defs>
            </svg>

            <h3 className="text-lg font-semibold">Trusted transporters</h3>
            <p className="text-gray-600">
              See background check status, cancellation rates, customer feedback
              and more.
            </p>
          </div>
        </div>
      </div>

      <div>
        {!!cars?.length && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4">
              {cars.length === 1 ? "Vehicle" : "Vehicles"}
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {cars.map((car) => {
                const primaryImage = car.images?.[0]
                const title = [
                  car.variant_year,
                  formatMakeName(car.make_name),
                  car.model_name,
                ]
                  .filter(Boolean)
                  .join(" ")
                const metaParts = [car.variant_type, car.metadata?.color]
                  .filter(Boolean)
                  .join(" · ")

                return (
                  <div
                    key={car.id}
                    className="flex gap-4 rounded-xl border border-gray15 p-4"
                  >
                    {primaryImage ? (
                      <div className="relative h-28 w-28 flex-shrink-0">
                        <Image
                          src={primaryImage}
                          alt={title}
                          fill
                          sizes="112px"
                          className="rounded-lg object-cover"
                        />
                      </div>
                    ) : (
                      <div className="flex h-28 w-28 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50">
                        <svg
                          width="40"
                          height="40"
                          viewBox="0 0 40 40"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden
                        >
                          <g clipPath="url(#clip0_4013_5324)">
                            <path
                              d="M20 40C31.0457 40 40 31.0457 40 20C40 8.9543 31.0457 0 20 0C8.9543 0 0 8.9543 0 20C0 31.0457 8.9543 40 20 40Z"
                              fill="#E2F1FC"
                            />
                            <path
                              d="M14.5358 26.3768V25.5614H25.464V26.3768C25.4687 27.4639 26.3526 28.3435 27.4396 28.3435H28.6243C29.7113 28.3435 30.5952 27.4644 30.5999 26.3773V19.9018C30.5952 19.1713 30.2978 18.4725 29.7677 17.9647L29.3257 17.4967L27.7263 13.1856C27.438 12.415 26.7006 11.9046 25.8787 11.8999H14.4044C13.5816 11.8999 12.8445 12.4152 12.5562 13.1854L10.9805 17.4395L10.3399 18.0322C9.74423 18.5402 9.3999 19.2853 9.3999 20.0678V26.3768C9.40463 27.4639 10.2885 28.3435 11.3755 28.3435H12.5602C13.6472 28.3435 14.5311 27.4639 14.5358 26.3768ZM28.6251 24.464H11.3755C10.8964 24.468 10.5016 24.0858 10.4973 23.6033V20.0678C10.4973 19.6039 10.7041 19.1606 11.0587 18.8609L11.0648 18.8552L11.0817 18.8384L11.662 18.2922H28.5718L28.9676 18.7169C28.9732 18.7243 28.9787 18.7299 28.9828 18.7337C28.9845 18.7353 28.9862 18.7367 28.9878 18.7381L28.9997 18.7494C29.3165 19.0494 29.498 19.4674 29.498 19.9018V23.6037V23.6843C29.4529 24.1245 29.0766 24.4678 28.6251 24.464ZM13.5871 13.5608C13.587 13.5608 13.5871 13.5607 13.5871 13.5608C13.7158 13.219 14.0417 12.9973 14.4044 12.9973H25.8826C26.2447 12.9973 26.5709 13.2227 26.6999 13.5608C26.6999 13.5607 26.6999 13.5609 26.6999 13.5608L28.0497 17.1948H27.2332C26.9387 15.5037 25.3465 14.3472 23.6378 14.6023C22.2811 14.802 21.2089 15.8503 20.9737 17.1948H12.2373L13.5871 13.5608ZM24.5293 15.7054C25.2936 15.8681 25.8997 16.4439 26.1055 17.1948H22.0921C22.3785 16.1387 23.4437 15.4785 24.5293 15.7054ZM13.4384 26.3764C13.4341 26.8596 13.0437 27.2461 12.5602 27.2461H11.3755C10.892 27.2461 10.5016 26.8597 10.4973 26.3764V25.3723C10.7593 25.5005 11.0695 25.5614 11.3755 25.5614H13.4384V26.3764ZM29.5025 26.3764C29.4982 26.8597 29.1078 27.2461 28.6243 27.2461H27.4396C26.9564 27.2461 26.5657 26.8555 26.5614 26.3764V25.5614H28.6243C28.9303 25.5614 29.2405 25.5005 29.5025 25.3723V26.3764Z"
                              fill="#1A1A1A"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M25 22.3C25.4418 22.3 25.8 21.9418 25.8 21.5C25.8 21.0582 25.4418 20.7 25 20.7C24.5582 20.7 24.2 21.0582 24.2 21.5C24.2 21.9418 24.5582 22.3 25 22.3ZM25 23.5C26.1046 23.5 27 22.6046 27 21.5C27 20.3954 26.1046 19.5 25 19.5C23.8954 19.5 23 20.3954 23 21.5C23 22.6046 23.8954 23.5 25 23.5Z"
                              fill="#1A1A1A"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M15 22.3C15.4418 22.3 15.8 21.9418 15.8 21.5C15.8 21.0582 15.4418 20.7 15 20.7C14.5582 20.7 14.2 21.0582 14.2 21.5C14.2 21.9418 14.5582 22.3 15 22.3ZM15 23.5C16.1046 23.5 17 22.6046 17 21.5C17 20.3954 16.1046 19.5 15 19.5C13.8954 19.5 13 20.3954 13 21.5C13 22.6046 13.8954 23.5 15 23.5Z"
                              fill="#1A1A1A"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_4013_5324">
                              <rect width="40" height="40" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold">{title}</h3>
                      {metaParts && (
                        <p className="text-sm text-gray-500 capitalize">
                          {metaParts}
                        </p>
                      )}
                      {car.metadata?.details && (
                        <VehicleDetails details={car.metadata.details} />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {!!reviews?.length && <QuoteCTA ctaUrl={ctaUrl} ctaLabel={ctaLabel} />}
        {!!reviews?.length && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Customer Reviews</h2>
            <div className="space-y-6">
              {reviews.map((review: Reviews[number]) => {
                const authorName = normalizeReviewAuthorName(review.authorName)

                return (
                  <div
                    key={review.shipmentId}
                    data-review-id={review.shipmentId}
                    className="border-b pb-6 last-of-type:border-0"
                  >
                    <div className="flex justify-between mb-2">
                      <div>
                        {authorName && (
                          <h3
                            data-review-author
                            className="text-lg font-semibold"
                          >
                            {authorName}
                          </h3>
                        )}
                        <p className="text-gray-500 text-sm">
                          {review.createdAt ? (
                            <RelativeTime
                              date={review.createdAt}
                              formatRelative={formatReviewDate}
                            />
                          ) : (
                            "recently"
                          )}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        {[...Array(review.rating)].map((_, i) => (
                          <AiFillStar
                            key={i}
                            className="h-5 w-5 text-yellow-400"
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-2">
                      {typeof review.content === "string"
                        ? redactEmails(review.content)
                        : ""}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
      <QuoteCTA ctaUrl={ctaUrl} ctaLabel={ctaLabel} />
    </section>
  )
}

export default PetTravelPage
